import {Badge, Button, Card, Group, Loader, Modal, Stack, Table} from '@mantine/core'
import {useForm} from '@mantine/form'
import {notifications} from '@mantine/notifications'
import {IconPlus} from '@tabler/icons-react'
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {createFileRoute, Link} from '@tanstack/react-router'
import {useState} from 'react'

import {createOrganization, listOrganizations} from 'src/api/admin/api'
import type {CreateOrganizationRequest} from 'src/api/admin/models/organization'
import {
    numberInputToNumber,
    OrganizationSettingsFormFields,
    type OrganizationSettingsFormValues,
    organizationSettingsValidators,
} from 'src/components/admin/organization-settings-form'

export const Route = createFileRoute('/_authed/admin/organizations/')({
    component: AdminOrganizationsPage,
})

function AdminOrganizationsPage() {
    const queryClient = useQueryClient()
    const [createModalOpen, setCreateModalOpen] = useState(false)

    const {data: orgs = [], isLoading} = useQuery({
        queryKey: ['admin', 'organizations'],
        queryFn: () => listOrganizations(),
    })

    const createMutation = useMutation({
        mutationFn: (body: CreateOrganizationRequest) => createOrganization(body),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['admin', 'organizations']})
            setCreateModalOpen(false)
            notifications.show({color: 'green', message: 'Organization created'})
        },
        onError: (err: Error) => {
            notifications.show({color: 'red', message: err.message})
        },
    })

    return (
        <Card withBorder padding="md">
            <Card.Section withBorder inheritPadding py="md">
                <Group justify="space-between">
                    <span>Organizations</span>
                    <Button
                        leftSection={<IconPlus size={16} />}
                        onClick={() => setCreateModalOpen(true)}
                    >
                        Create Organization
                    </Button>
                </Group>
            </Card.Section>
            <Card.Section inheritPadding pt="md">
                {isLoading ? (
                    <Loader size="sm" />
                ) : (
                    <Table>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>Name</Table.Th>
                                <Table.Th>Created</Table.Th>
                                <Table.Th>Status</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {orgs.map((org) => (
                                <Table.Tr key={org.id}>
                                    <Table.Td>
                                        <Link
                                            to="/admin/organizations/$orgId"
                                            params={{orgId: org.id}}
                                            style={{
                                                color: 'inherit',
                                                textDecoration: 'none',
                                                fontWeight: 500,
                                            }}
                                        >
                                            {org.name}
                                        </Link>
                                    </Table.Td>
                                    <Table.Td>
                                        {new Date(org.createdAt).toLocaleDateString()}
                                    </Table.Td>
                                    <Table.Td>
                                        <Badge color={org.deactivatedAt ? 'red' : 'green'}>
                                            {org.deactivatedAt ? 'Deactivated' : 'Active'}
                                        </Badge>
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                )}
            </Card.Section>

            <Modal
                title="Create Organization"
                opened={createModalOpen}
                onClose={() => setCreateModalOpen(false)}
            >
                <CreateOrgForm
                    onSubmit={(values) => createMutation.mutate(values)}
                    onCancel={() => setCreateModalOpen(false)}
                    loading={createMutation.isPending}
                />
            </Modal>
        </Card>
    )
}

type CreateOrgFormProps = {
    onSubmit: (values: CreateOrganizationRequest) => void
    onCancel: () => void
    loading: boolean
}

function CreateOrgForm({onSubmit, onCancel, loading}: CreateOrgFormProps) {
    const form = useForm<OrganizationSettingsFormValues>({
        initialValues: {
            name: '',
            campaignGoal: 5_000_000,
            toplinePercentage: 20,
            engagementBasis: 'mlr',
        },
        validate: organizationSettingsValidators,
    })

    return (
        <form
            onSubmit={form.onSubmit((values) =>
                onSubmit({
                    name: values.name.trim(),
                    campaignGoal: numberInputToNumber(values.campaignGoal),
                    toplinePercentage: numberInputToNumber(values.toplinePercentage),
                    useRfmForEngagement: values.engagementBasis === 'rfm',
                }),
            )}
        >
            <Stack>
                <OrganizationSettingsFormFields form={form} />
                <Group>
                    <Button variant="default" onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button type="submit" loading={loading}>
                        Create
                    </Button>
                </Group>
            </Stack>
        </form>
    )
}
